// Angular
import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

// Angular Material
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Third party
import * as _ from 'lodash';
import { Observable, Subscription, takeUntil } from 'rxjs';

// CanvasJS ships a UMD build with no TypeScript typings; @ts-ignore
// suppresses the TS7016 raised by the stricter ng-packagr build pass.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore -- @canvasjs/charts ships without TypeScript typings
import CanvasJS from '@canvasjs/charts';

// Local library
import {
  ConvertTimeToMinutesPipe,
  ConvertToRangePipe,
  MatNotificationService,
  MatrixDataPipe,
  NumberWithCommasPipe,
  ToAnyPipe,
  UnsubscribeObservableService,
TranslatePipe
} from '@amwager/util';
import {
  GUEST,
  IGetEventsDetailsResponse,
  IPoolMatrixToteResponse,
  IToteMatrixSlimModel,
  IWillPaysToteResponse,
  TRACK_TYPE_lIST,
} from '@amwager/util-ts';
import {
  IPoolsTotalsBuildResult,
  ISelectedRace,
  ISelectedRaceAllOddsMatrixes,
  ISelectedRaceMatrix,
  ISelectedRacePoolsTotals,
  ISelectedRaceWillPays,
  IWPSPoolsTotals,
  MatrixService,
  WagerFacade,
  WagerService,
} from '@amwager/wager/data-access';
import {
  CommonFacade,
  PermissionService,
  SessionService,
} from '@amwager/data-access';

/**
 * Wager Pool Data panel — Odds/Totals/EX/QU/DBL/WillPays tabs
 */
@Component({
  selector: 'feature-mat-matrix',
  templateUrl: './matrix.component.html',
  styleUrls: ['./matrix.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatrixDataPipe,
    NumberWithCommasPipe,
    ToAnyPipe,
    ConvertToRangePipe,
    ConvertTimeToMinutesPipe,
    TranslatePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatrixComponent
  extends UnsubscribeObservableService
  implements OnInit, OnChanges, OnDestroy, AfterViewChecked {

  /** Optional pre-seed for the selected race (avoids empty-state flash) */
  @Input() selectedRaceInput?: ISelectedRace;

  /** Optional pre-seed for the selected event track */
  @Input() selectedEventInput?: IGetEventsDetailsResponse;

  /** Panel header title */
  @Input() title: string = 'Pool Data';

  /** Online state piped down from the wager shell */
  @Input() isOnline: boolean = true;

  /** Latest race status observed by the parent surface */
  @Input() currentRaceStatus?: number;

  /**
   * Results page shows replays only — when true the Video chip opens
   * the REPLAY video instead of the live video.
   */
  @Input() isReplayVideo: boolean = false;

  /**
   * Delegates the live-video open to the parent (wager page), which is
   * the single owner of the live-video dialog. Prevents duplicate dialogs.
   */
  @Output() playVideoRequested = new EventEmitter<void>();

  /**
   * Delegates the replay-video open to the parent (results page), the
   * single owner of the replay dialog.
   */
  @Output() replayVideoRequested = new EventEmitter<void>();

  readonly ODDS = 'Odds';
  readonly TOTALS = 'Totals';
  readonly WIN = 'WIN';
  readonly PLC = 'PLC';
  readonly SHW = 'SHW';
  readonly TRU = 'TRU';
  readonly EX = 'EX';
  readonly QU = 'QU';
  readonly DBL = 'DBL';
  readonly WILL_PAYS = 'WillPays';
  readonly WIN_CHART = 'Win Chart';
  readonly WIN_TABLE = 'Win Table';
  readonly TRACK_TYPE_lIST = TRACK_TYPE_lIST;

  /** Currently visible tab. */
  tab: string = this.ODDS;

  /** Pool ids surfaced by the matrix tabs (always includes `TRU`). */
  poolList: string[] = ['TRU'];

  /** Runners list driven by the selected race observer. */
  runners: any[] = [];

  /** Will-pays payload for the WillPays tab. */
  willPays!: IWillPaysToteResponse;

  /** Currently observed race (kept for template bindings). */
  selectedRace?: ISelectedRace;

  /** Decorated pool matrices keyed by pool id (`WIN`, `PLC`, ...). */
  matrix: any;

  /**
   * Last accepted *raw* ToteResponse per pool. The decorated `matrix` is
   * always rebuilt from this, so a rejected payload can't wipe a pool and
   * EX/QU/DBL never get decorated twice.
   */
  private matrixRaw: Record<string, IPoolMatrixToteResponse | undefined> = {};

  // Last payloads applied — skip no-op repaints when a refresh
  // (25s poll / pusher) returns data identical to what's on screen.
  private lastPoolsTotalsRaw?: unknown;
  private lastWillPaysRaw?: unknown;
  private lastOddsMatrixesRaw?: unknown;

  /** Single-leg WIN / PLC / SHW pool totals dict. */
  singleLegDict!: IWPSPoolsTotals;
  /** Multi-position pool totals dict (EX, QU, DBL, ...). */
  multiLegDict: any = {};
  /** Multi-race pool totals dict (DBL, PK4, ...). */
  multiRaceDict: any = {};

  /** Win Table rows (latest 5 unique odds snapshots, newest first). */
  winTable: any[] = [];

  /** Active filter on the Win Chart tab */
  winChartFilterButtons = {
    WIN: true,
    TRU: false,
    BOTH: false,
  };

  /** CanvasJS options rebuilt on filter / data change */
  winChartOptions: any = {};

  /** True once a non-empty options payload has been built */
  winChartHasData = false;

  /** Toggles the loading spinner on the Win Chart tab */
  winChartLoad = false;

  /** Chart container div (only present on the Win Chart tab) */
  @ViewChild('winChartContainer', { static: false })
  winChartContainerRef?: ElementRef<HTMLDivElement>;

  /** Live CanvasJS chart instance held for in-place updates */
  private winChartInstance: any = null;

  /** Deferred render flag drained by ngAfterViewChecked */
  private winChartRenderPending = false;

  /** Currently observed event track */
  selectedEvent?: IGetEventsDetailsResponse;

  private raceAllOddsMatrixes?: {
    WIN?: IToteMatrixSlimModel[];
    TRU?: IToteMatrixSlimModel[];
  };
  private getRaceAllOddsMatrixesSubs?: Subscription;

  /**
   * Flashes true for a short window whenever the selected event
   * or race changes — gives the tabs / tables a skeleton overlay
   * while the store clears old data and refetches fresh values.
   */
  showSelectionSkeleton = false;

  /** Timer handle for the skeleton flash; cleared on destroy. */
  private selectionSkeletonTimeoutId?: ReturnType<typeof setTimeout>;

  /**
   * `<eventId>-<raceId>` of the last flash. Prevents the skeleton
   * from re-firing when the same emission bubbles through twice
   * (both selectedEvent and selectedRace observers land on mount).
   */
  private lastSelectionKey?: string;

  constructor(
    private readonly wagerFacade: WagerFacade,
    private readonly matrixService: MatrixService,
    private readonly wagerService: WagerService,
    private readonly commonFacade: CommonFacade,
    private readonly permissionService: PermissionService,
    private readonly sessionService: SessionService,
    private readonly matNotificationService: MatNotificationService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    private readonly ngZone: NgZone,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.selectedEventInput) this.selectedEvent = this.selectedEventInput;
    if (this.selectedRaceInput) {
      this.selectedRace = this.selectedRaceInput;
      this.runners = this.selectedRaceInput?.Runners || [];
      this.poolList = this.matrixService.buildPoolList(this.selectedRaceInput);
    }

    this.observerGetSelectedEventTrack();
    this.observerGetSelectedRace();
    this.observerGetRaceMatrix();
    this.observerGetRacePoolsTotals();
    this.observerGetRaceWillPays();
    this.observerGetRaceAllOddsMatrixes();
  }

  /**
   * Results page freezes the matrix to the selected finished race via
   * inputs (live store subscriptions are skipped in replay mode), so
   * apply input changes here to follow race chip clicks.
   * @param {SimpleChanges} changes Changed inputs
   * @returns {void}
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isReplayVideo) return;

    if (changes['selectedEventInput'] && this.selectedEventInput) {
      this.selectedEvent = this.selectedEventInput;
    }
    if (changes['selectedRaceInput'] && this.selectedRaceInput) {
      const previousRaceId = this.selectedRace?.RaceId;

      this.selectedRace = this.selectedRaceInput;
      this.runners = this.selectedRaceInput?.Runners || [];
      this.poolList = this.matrixService.buildPoolList(this.selectedRaceInput);

      // New race — forget the previous race's cached payloads.
      if (previousRaceId !== this.selectedRaceInput?.RaceId) {
        this.resetCachedPayloads();
      }
    }
    this.cdRef.markForCheck();
  }

  override ngOnDestroy(): void {
    this.toUnsubscribe();
    this.getRaceAllOddsMatrixesSubs?.unsubscribe();
    // OLD: this.winChartInstance = null; — leaked the chart's resize listener.
    this.destroyWinChart();

    if (this.selectionSkeletonTimeoutId) {
      clearTimeout(this.selectionSkeletonTimeoutId);
      this.selectionSkeletonTimeoutId = undefined;
    }
  }

  /**
   * Results page only — reject payloads belonging to another race.
   * The matrix slices are shared with the live wager page, so pusher
   * keeps streaming the current race's pools into them. Permissive when
   * the payload carries no identifiers, so valid data is never dropped.
   * @param {object} payload Tote response carrying `EventId` / `RaceId`
   * @returns {boolean} true when the payload matches the frozen selection
   */
  private belongsToSelection(
    payload?: { EventId?: string; RaceId?: number } | null
  ): boolean {
    if (!this.isReplayVideo) return true;
    if (!payload) return true;

    const eventId = this.selectedEvent?.ToteResponse?.EventDetail?.EventId;
    const raceId = this.selectedRace?.RaceId;

    if (payload?.EventId && eventId && payload.EventId !== eventId) return false;
    if (payload?.RaceId && raceId && payload.RaceId !== raceId) return false;

    return true;
  }

  /**
   * Pool matrix source. The results page reads the replay-only mirror,
   * which nothing but its own fetch writes to, so live pusher updates to
   * the shared slice can never blank or repaint its tables.
   * @returns {Observable} matrix slice for the current mode
   */
  private raceMatrixSource$(): Observable<ISelectedRaceMatrix | undefined> {
    if (this.isReplayVideo) return this.wagerFacade.getReplayRaceMatrix$();
    return this.wagerFacade.getRaceMatrix$();
  }

  /**
   * Pools-totals source — replay mirror on the results page
   * @returns {Observable} pools-totals slice for the current mode
   */
  private racePoolsTotalsSource$(): Observable<ISelectedRacePoolsTotals | undefined> {
    if (this.isReplayVideo) return this.wagerFacade.getReplayRacePoolsTotals$();
    return this.wagerFacade.getRacePoolsTotals$();
  }

  /**
   * Will-pays source — replay mirror on the results page
   * @returns {Observable} will-pays slice for the current mode
   */
  private raceWillPaysSource$(): Observable<ISelectedRaceWillPays | undefined> {
    if (this.isReplayVideo) return this.wagerFacade.getReplayRaceWillPays$();
    return this.wagerFacade.getRaceWillPays$();
  }

  /**
   * Odds-matrixes source — replay mirror on the results page
   * @returns {Observable} odds-matrixes slice for the current mode
   */
  private raceAllOddsMatrixesSource$(): Observable<{
    WIN?: ISelectedRaceAllOddsMatrixes;
    TRU?: ISelectedRaceAllOddsMatrixes;
  } | undefined> {
    if (this.isReplayVideo) return this.wagerFacade.getReplayRaceAllOddsMatrixes$();
    return this.wagerFacade.getRaceAllOddsMatrixes$();
  }

  /**
   * Whether a Win Chart / Win Table pool slot may be applied. On the
   * results page only our own (`replayMatrix`-tagged) fetch qualifies.
   * @param {ISelectedRaceAllOddsMatrixes} slot Odds-matrixes pool slot
   * @returns {boolean} true when the slot may be applied
   */
  private acceptOddsPool(slot?: ISelectedRaceAllOddsMatrixes): boolean {
    return !this.isReplayVideo || slot?.replayMatrix === true;
  }

  /**
   * Clear the Win Chart / Win Table back to their empty state
   * @returns {void}
   */
  private resetWinChart(): void {
    this.raceAllOddsMatrixes = undefined;
    this.winTable = [];
    this.winChartHasData = false;
    this.winChartOptions = {};
    // OLD: this.winChartInstance = null;
    this.destroyWinChart();
    this.lastOddsMatrixesRaw = undefined;
    this.cdRef.markForCheck();
  }

  /**
   * Drop cached payloads so a new race can't inherit the previous
   * selection's odds / totals / will-pays.
   * @returns {void}
   */
  private resetCachedPayloads(): void {
    this.matrixRaw = {};
    this.lastPoolsTotalsRaw = undefined;
    this.lastWillPaysRaw = undefined;
    this.lastOddsMatrixesRaw = undefined;
  }

  /**
   * Flip the skeleton on for ~600 ms whenever the (event, race)
   * pair changes. Guards against re-firing on duplicate emissions.
   * @param {string | undefined} eventId Current event id
   * @param {number | string | undefined} raceId Current race id
   * @returns {void}
   */
  private flashSelectionSkeleton(
    eventId?: string,
    raceId?: number | string
  ): void {
    const key = `${eventId ?? ''}-${raceId ?? ''}`;
    if (!eventId && !raceId) return;
    if (key === this.lastSelectionKey) return;

    this.lastSelectionKey = key;
    this.showSelectionSkeleton = true;
    this.cdRef.markForCheck();

    if (this.selectionSkeletonTimeoutId) {
      clearTimeout(this.selectionSkeletonTimeoutId);
    }
    this.selectionSkeletonTimeoutId = setTimeout(() => {
      this.showSelectionSkeleton = false;
      this.selectionSkeletonTimeoutId = undefined;
      this.cdRef.markForCheck();
    }, 600);
  }

  /**
   * Drain pending Win Chart render after the container attaches
   * @returns {void}
   */
  ngAfterViewChecked(): void {
    if (!this.winChartRenderPending) return;
    if (!this.winChartContainerRef?.nativeElement) return;

    this.winChartRenderPending = false;
    this.renderWinChart();
  }

  /**
   * Switch to a different tab
   * @param {string} type Tab identifier to activate
   * @returns {void}
   */
  selectTabMenu(type: string): void {
    this.tab = type;

    if (type === this.WIN_CHART) {
      // The *ngIf rebuilds the container, so the old chart must go with it.
      // OLD: this.winChartInstance = null;
      this.destroyWinChart();
      if (this.winChartHasData) this.winChartRenderPending = true;
    }

    this.cdRef.markForCheck();
  }

  /**
   * Toggle the Win Chart pool filter and rebuild chart options
   * @param {'WIN' | 'TRU' | 'BOTH'} pool Filter to activate
   * @returns {void}
   */
  winChartFilter(pool: 'WIN' | 'TRU' | 'BOTH'): void {
    this.winChartLoad = true;
    this.winChartFilterButtons.WIN = false;
    this.winChartFilterButtons.TRU = false;
    this.winChartFilterButtons.BOTH = false;
    this.winChartFilterButtons[pool] = true;

    // OLD: this.winChartInstance = null;
    this.destroyWinChart();

    if (pool === 'BOTH') this.buildBothChart();
    else this.buildSingleChart(pool);

    setTimeout(() => {
      this.winChartLoad = false;
      if (this.winChartHasData) this.winChartRenderPending = true;
      this.cdRef.detectChanges();
    }, 300);
  }

  /**
   * Check if live video is allowed for the matrix Video chip
   * @returns {boolean} true if allowed
   */
  isLiveVideoAllowed(): boolean {
    return this.wagerService.isLiveVideoAllowed(
      this.selectedRace,
      this.selectedEvent
    );
  }

  /** True when the race is final/done (RaceStatus 3=Final, 4=done). */
  isRaceFinal(): boolean {
    const status = this.selectedRace?.RaceStatus;
    return status === 3 || status === 4;
  }

  /** Replay is offered once the race is final and a selection exists. */
  isReplayVideoAllowed(): boolean {
    return Boolean(this.selectedEvent && this.selectedRace && this.isRaceFinal());
  }

  /** Unified enable state for the Video chip (replay on results, else live). */
  isVideoAllowed(): boolean {
    return this.isReplayVideo ? this.isReplayVideoAllowed() : this.isLiveVideoAllowed();
  }

  /**
   * Play race video
   * @returns {void}
   */
  async playVideo(): Promise<void> {
    // Results page shows replays only — delegate to the parent's
    // replay dialog and skip the live-video path entirely.
    if (this.isReplayVideo) {
      if (this.isReplayVideoAllowed()) this.replayVideoRequested.emit();
      return;
    }

    if (!this.isLiveVideoAllowed()) return;
    if (this.selectedRace?.RaceStatus === 3) return;

    if (!this.isOnline) {
      this.commonFacade.setIsConnectedInternet('MatrixComponent:playVideo', false);
    }

    if (!this.hasPermission()) {

      if (this.sessionService.getUserEmail() === GUEST || !this.sessionService.hasToken()) {
        this.router.navigate(['login']);
      }
      else {
        this.showAlertInfo('You are not an Identified User.');
      }

      return;
    }

    // Delegate to the parent (wager page) which owns the single
    // live-video dialog + store subscription — avoids duplicate dialogs.
    if (this.selectedEvent && this.currentRaceStatus !== 3) {
      this.playVideoRequested.emit();
    }

    // OLD: matrix fetched the link itself but never rendered it (no
    // subscription/dialog here), so the Video button did nothing.
    // if (this.selectedEvent) {
    //   if (this.currentRaceStatus !== 3) {
    //     this.wagerFacade.getEventRaceVideoLive(
    //       'MatrixComponent:playVideo',
    //       {
    //         toteEventId: this.selectedEvent?.ToteResponse?.EventDetail?.EventId,
    //         width: 720,
    //         height: 576
    //       },
    //       this.selectedEvent?.VideoType === 'IGB' ? 'IGB' : 'default'
    //     );
    //   }
    // }
  }

  /**
   * Check if user role is allowed. Mirrors `WagerComponent.hasPermission`.
   * @returns {boolean} true/false
   */
  hasPermission(): boolean {
    if (this.permissionService.isAllowed()) {
      return true;
    }

    this.router.navigate(['/login']);
    return false;
  }

  /**
   * Show an info alert
   * @param {string} message alert message
   * @returns {Promise<void>}
   */
  async showAlertInfo(message: string): Promise<void> {
    this.matNotificationService.showAlert('Info', message, '', '', 'Close');
  }

  /**
   * Observe the currently selected event track
   * @returns {void}
   */
  private observerGetSelectedEventTrack(): void {
    this.wagerFacade.getSelectedEventTrack$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((selectedEventTrack?: IGetEventsDetailsResponse) => {
        // Results page is frozen to its inputs — ignore live store changes.
        if (this.isReplayVideo) return;
        if (!selectedEventTrack) return;
        this.selectedEvent = selectedEventTrack;
      });
  }

  private observerGetSelectedRace(): void {
    this.wagerFacade.getSelectedRace$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((race?: ISelectedRace) => {
        // Results page is frozen to its inputs — ignore live store changes
        // so pusher / event-detail refreshes can't retarget the matrix.
        if (this.isReplayVideo) return;

        if (!race || !race?.RaceId) return;

        const previousRaceId = this.selectedRace?.RaceId;

        this.selectedRace = race;
        this.runners = race?.Runners || [];
        this.poolList = this.matrixService.buildPoolList(race);

        // New race — forget the previous race's cached payloads.
        if (previousRaceId !== race?.RaceId) this.resetCachedPayloads();

        // Active EX/QU/DBL tab may not exist in the new race — fall
        // back to Odds so we never strand the user on an empty panel.
        if (
          (this.tab === this.EX || this.tab === this.QU || this.tab === this.DBL)
          && !this.poolList.includes(this.tab)
        ) {
          this.tab = this.ODDS;
        }

        // Skeleton flash on (event, race) change — auto-clears.
        this.flashSelectionSkeleton(
          this.selectedEvent?.id,
          race?.RaceId
        );

        this.cdRef.markForCheck();
      });
  }

  private observerGetRaceMatrix(): void {
    // OLD: always the live slice, which pusher keeps rewriting.
    // this.wagerFacade.getRaceMatrix$()
    this.raceMatrixSource$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((matrix?: ISelectedRaceMatrix) => {
        if (_.isEmpty(matrix)) {
          // Store cleared — drop stale odds so a race/date switch
          // doesn't leave the previous selection's data on screen.
          this.matrix = undefined;
          this.matrixRaw = {};
          this.cdRef.markForCheck();
          return;
        }

        // OLD: took every emission as-is, so live pusher pools written to
        // the shared slice repainted the results page's frozen race.
        // const nextMatrix: any = {};
        // _.forEach(this.poolList, (poolId: string) => {
        //   try {
        //     nextMatrix[poolId] = matrix?.[poolId]?.response?.ToteResponse;
        //   } catch {
        //     nextMatrix[poolId] = null;
        //   }
        // });
        const nextMatrix: any = {};
        _.forEach(this.poolList, (poolId: string) => {
          try {
            const toteResponse = matrix?.[poolId]?.response?.ToteResponse;

            // Keep the last accepted payload when this one is for
            // another race, so the pool doesn't blank out.
            if (this.belongsToSelection(toteResponse)) {
              this.matrixRaw[poolId] = toteResponse;
            }

            nextMatrix[poolId] = this.matrixRaw[poolId];
          } catch {
            nextMatrix[poolId] = null;
          }
        });

        let decorated = nextMatrix;
        decorated = this.matrixService.updateMatrix(decorated, 'EX');
        decorated = this.matrixService.updateMatrix(decorated, 'QU');
        decorated = this.matrixService.updateMatrix(decorated, 'DBL');

        // Identical data — skip the repaint so the tabs stay put.
        if (_.isEqual(this.matrix, decorated)) return;

        this.matrix = decorated;
        this.cdRef.markForCheck();
      });
  }

  private observerGetRacePoolsTotals(): void {
    // OLD: always the live slice, which pusher keeps rewriting.
    // this.wagerFacade.getRacePoolsTotals$()
    this.racePoolsTotalsSource$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((poolsTotals?: ISelectedRacePoolsTotals) => {
        const racePoolsTotals = poolsTotals?.response?.ToteResponse;
        if (_.isEmpty(racePoolsTotals)) {
          // Store cleared — reset pool-total dicts.
          this.singleLegDict = {} as IWPSPoolsTotals;
          this.multiLegDict = {};
          this.multiRaceDict = {};
          this.lastPoolsTotalsRaw = undefined;
          this.cdRef.markForCheck();
          return;
        }

        // Results page: totals streamed in for the live race aren't ours.
        if (!this.belongsToSelection(racePoolsTotals)) return;

        // Identical data — skip the repaint so the tabs stay put.
        if (_.isEqual(this.lastPoolsTotalsRaw, racePoolsTotals)) return;
        this.lastPoolsTotalsRaw = racePoolsTotals;

        const result: IPoolsTotalsBuildResult =
          this.matrixService.buildPoolsTotals(
            racePoolsTotals,
            this.selectedRace,
            this.selectedEvent
          );

        this.singleLegDict = result.singleLegDict;
        this.multiLegDict = result.multiLegDict;
        this.multiRaceDict = result.multiRaceDict;

        this.cdRef.markForCheck();
      });
  }

  private observerGetRaceWillPays(): void {
    // OLD: always the live slice, which pusher keeps rewriting.
    // this.wagerFacade.getRaceWillPays$()
    this.raceWillPaysSource$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((willPaysResponse?: ISelectedRaceWillPays) => {
        const willPays = willPaysResponse?.response?.ToteResponse;
        if (_.isEmpty(willPays)) {
          // Store cleared — drop stale will-pays.
          this.willPays = undefined as unknown as IWillPaysToteResponse;
          this.lastWillPaysRaw = undefined;
          this.cdRef.markForCheck();
          return;
        }

        // Results page: will-pays for the live race aren't ours.
        if (!this.belongsToSelection(willPays)) return;

        // Identical data — skip the repaint so the tabs stay put.
        if (_.isEqual(this.lastWillPaysRaw, willPays)) return;
        this.lastWillPaysRaw = willPays;

        this.willPays = willPays as IWillPaysToteResponse;
        this.cdRef.markForCheck();
      });
  }

  /**
   * Feed Win Chart and Win Table from the odds-matrixes slice
   * @returns {void}
   */
  private observerGetRaceAllOddsMatrixes(): void {
    this.getRaceAllOddsMatrixesSubs?.unsubscribe();
    // OLD: always the live slice, which pusher keeps rewriting.
    // this.getRaceAllOddsMatrixesSubs = this.wagerFacade.getRaceAllOddsMatrixes$()
    this.getRaceAllOddsMatrixesSubs = this.raceAllOddsMatrixesSource$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((raceAllOddsMatrixes?: {
        WIN?: ISelectedRaceAllOddsMatrixes;
        TRU?: ISelectedRaceAllOddsMatrixes;
      }) => {
        // Store cleared on a race/date switch — always honour it.
        if (_.isEmpty(raceAllOddsMatrixes)) {
          this.resetWinChart();
          return;
        }

        // These payloads carry no race ids, so on the results page identity
        // has to come from the slice tag: our fetch sets `replayMatrix`,
        // live pusher writes don't. Keep the last accepted snapshot for any
        // pool a pusher write just overwrote.
        // OLD: took both pools unconditionally.
        // const winSlim = raceAllOddsMatrixes?.WIN?.response?.ToteMatrixSlim;
        // const truSlim = raceAllOddsMatrixes?.TRU?.response?.ToteMatrixSlim;
        const winSlim = this.acceptOddsPool(raceAllOddsMatrixes?.WIN)
          ? raceAllOddsMatrixes?.WIN?.response?.ToteMatrixSlim
          : this.raceAllOddsMatrixes?.WIN;
        const truSlim = this.acceptOddsPool(raceAllOddsMatrixes?.TRU)
          ? raceAllOddsMatrixes?.TRU?.response?.ToteMatrixSlim
          : this.raceAllOddsMatrixes?.TRU;

        if (_.isEmpty(winSlim) && _.isEmpty(truSlim)) {
          // OLD: cleared the same fields inline; resetWinChart() now owns it
          // so the chart instance is destroyed rather than just dropped.
          this.resetWinChart();
          return;
        }

        // Identical snapshots — skip the CanvasJS rebuild.
        const nextOddsMatrixes = { WIN: winSlim, TRU: truSlim };
        if (_.isEqual(this.lastOddsMatrixesRaw, nextOddsMatrixes)) return;
        this.lastOddsMatrixesRaw = nextOddsMatrixes;

        this.raceAllOddsMatrixes = nextOddsMatrixes;
        this.buildWinTable();

        if (this.winChartFilterButtons.BOTH) this.buildBothChart();
        else if (this.winChartFilterButtons.TRU) this.buildSingleChart('TRU');
        else this.buildSingleChart('WIN');

        this.cdRef.markForCheck();
      });
  }

  /**
   * Rebuild the Win Table from cached WIN matrix snapshots
   * @returns {void}
   */
  private buildWinTable(): void {
    this.winTable = this.matrixService.buildWinTable(
      this.raceAllOddsMatrixes?.WIN || []
    );
  }

  /**
   * Rebuild CanvasJS options for the single-pool Win Chart view
   * @param {'WIN' | 'TRU'} pool Pool to render
   * @returns {void}
   */
  private buildSingleChart(pool: 'WIN' | 'TRU'): void {
    const source = pool === 'WIN'
      ? this.raceAllOddsMatrixes?.WIN
      : this.raceAllOddsMatrixes?.TRU;

    const arrayData = this.matrixService.buildWinChartArrayData(
      source,
      this.selectedRace
    );

    this.winChartOptions = this.matrixService.buildCanvasJsOptions(
      arrayData,
      this.selectedRace?.TrackType,
      pool
    );

    // OLD: counted point-less series as data.
    // this.winChartHasData = !_.isEmpty(this.winChartOptions?.data);
    this.winChartHasData = this.hasPlotableSeries();
    if (this.winChartHasData) this.winChartRenderPending = true;
  }

  /**
   * Rebuild CanvasJS options for the combined WIN + TRU Win Chart view
   * @returns {void}
   */
  private buildBothChart(): void {
    const arrayData = this.matrixService.buildBothChartArrayData(
      this.raceAllOddsMatrixes?.WIN,
      this.raceAllOddsMatrixes?.TRU,
      this.selectedRace
    );

    this.winChartOptions = this.matrixService.buildCanvasJsOptions(
      arrayData,
      this.selectedRace?.TrackType,
      'BOTH'
    );

    // OLD: counted point-less series as data.
    // this.winChartHasData = !_.isEmpty(this.winChartOptions?.data);
    this.winChartHasData = this.hasPlotableSeries();
    if (this.winChartHasData) this.winChartRenderPending = true;
  }

  /**
   * Tear down the live CanvasJS instance. Each chart registers a window
   * resize listener, so dropping the reference alone leaks a handler that
   * keeps re-rendering into a container Angular already removed.
   * @returns {void}
   */
  private destroyWinChart(): void {
    const instance = this.winChartInstance;
    this.winChartInstance = null;
    if (!instance) return;

    // Never let teardown throw — it runs from ngOnDestroy and tab switches.
    try {
      if (_.isFunction(instance?.destroy)) instance.destroy();
    } catch {
      // Chart already detached; nothing left to release.
    }
  }

  /**
   * True only when at least one series carries a plotable point, so an
   * options blob full of empty series shows the no-odds message instead
   * of rendering bare, colourless axes.
   * @returns {boolean} true when the built options can be drawn
   */
  private hasPlotableSeries(): boolean {
    return _.some(
      this.winChartOptions?.data,
      (series: any) => !_.isEmpty(series?.dataPoints)
    );
  }

  /**
   * Render (or re-render) the CanvasJS chart against the current options
   * @returns {void}
   */
  private renderWinChart(): void {
    if (!CanvasJS?.Chart) return;
    const container = this.winChartContainerRef?.nativeElement;
    if (!container) return;
    if (!this.winChartHasData) return;

    this.ngZone.runOutsideAngular(() => {
      if (!this.winChartInstance) {
        this.winChartInstance = new CanvasJS.Chart(
          container,
          this.winChartOptions
        );
      } else {
        this.winChartInstance.options = this.winChartOptions;
      }

      this.winChartInstance.render();
    });
  }
}
